import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { RobotSettings as RobotSettingsModel, Department } from '../types';
import { brandsApi, categoriesApi, treeApi, departmentsApi } from '../services/api';
import { SectionCard, useToast } from '@vmcom/ui';

type RobotConfig = RobotSettingsModel;
type Scope = 'brands' | 'categories' | 'tree';

const defaultRobotFields = (): RobotConfig => ({
  enabled: false,
  maxGenerations: 20,
  textsPerRun: 5,
  maxConcurrentGenerations: 0,
  delayBetweenGenerations: 5000,
  autoRetry: true,
  retryAttempts: 3,
  customInstructions: '',
  departmentSiteHost: undefined,
});

const SCOPE_API = { brands: brandsApi, categories: categoriesApi, tree: treeApi };
const SCOPE_LABEL = { brands: 'Бренды', categories: 'Категории', tree: 'Ветки' };

interface RobotSettingsProps {
  scopes?: Scope[];
  activeCompanyId?: string;
}

const validateScope = (values: RobotConfig, scope: Scope): string | null => {
  if (values.maxGenerations < 1 || values.maxGenerations > 100) {
    return 'Максимальное количество генераций: от 1 до 100';
  }
  if (values.textsPerRun < 1 || values.textsPerRun > 50) {
    return 'Количество текстов за проход: от 1 до 50';
  }
  if (scope !== 'brands' && (values.maxConcurrentGenerations < 0 || values.maxConcurrentGenerations > 30)) {
    return 'Параллельных запросов: от 0 до 30';
  }
  if (values.delayBetweenGenerations < 1000 || values.delayBetweenGenerations > 60000) {
    return 'Задержка между генерациями: от 1000 до 60000 мс';
  }
  if (values.retryAttempts < 1 || values.retryAttempts > 10) {
    return 'Количество попыток повтора: от 1 до 10';
  }
  if ((values.customInstructions || '').length > 1000) {
    return 'Инструкции не должны превышать 1000 символов';
  }
  return null;
};

const RobotSettings: React.FC<RobotSettingsProps> = ({ scopes = ['brands', 'categories', 'tree'], activeCompanyId }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<Scope>(scopes[0] || 'brands');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [settingsByScope, setSettingsByScope] = useState<Record<Scope, RobotConfig>>(() => {
    const base = defaultRobotFields();
    return { brands: { ...base }, categories: { ...base }, tree: { ...base } };
  });

  const patchScope = (scope: Scope, patch: Partial<RobotConfig>) => {
    setSettingsByScope((prev) => ({ ...prev, [scope]: { ...prev[scope], ...patch } }));
  };

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [deptList, ...settingsList] = await Promise.all([
        departmentsApi.list(activeCompanyId),
        ...scopes.map((s) => SCOPE_API[s].getRobotSettings()),
      ]);

      setDepartments(deptList);
      const base = defaultRobotFields();
      const next: Record<Scope, RobotConfig> = { brands: { ...base }, categories: { ...base }, tree: { ...base } };
      scopes.forEach((s, i) => {
        next[s] = { ...base, ...settingsList[i] };
      });
      setSettingsByScope(next);
    } catch (err) {
      setError('Ошибка загрузки настроек робота');
      console.error('Error loading robot settings:', err);
    } finally {
      setLoading(false);
    }
  }, [scopes.join(','), activeCompanyId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSave = async () => {
    const scope = activeTab;
    const values = settingsByScope[scope];
    const validationError = validateScope(values, scope);
    if (validationError) {
      toast(validationError, 'warning');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await SCOPE_API[scope].updateRobotSettings(values);
      toast('Настройки робота успешно сохранены!', 'success');
    } catch (err) {
      setError('Ошибка сохранения настроек робота');
      console.error('Error saving robot settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderRobotFields = (scope: Scope) => {
    const values = settingsByScope[scope];
    const num = (v: string) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    return (
      <Stack spacing={2}>
        {scope !== 'brands' && (
          <SectionCard title={`Департамент для массовой генерации (${SCOPE_LABEL[scope]})`}>
            <FormControl fullWidth size="small">
              <InputLabel id={`dept-${scope}`}>Сайт по умолчанию</InputLabel>
              <Select
                labelId={`dept-${scope}`}
                label="Сайт по умолчанию"
                value={values.departmentSiteHost || ''}
                onChange={(e) =>
                  patchScope(scope, { departmentSiteHost: (e.target.value as string) || undefined })
                }
              >
                <MenuItem value="">
                  <em>Не выбран</em>
                </MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.site_host}>
                    {d.site_host} — {d.city_nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Если на панели генерации не выбран сайт и не включены «Все департаменты», бэкенд подставит этот домен.
            </Typography>
          </SectionCard>
        )}

        <SectionCard title="Основные настройки">
          <FormControlLabel
            control={
              <Switch checked={!!values.enabled} onChange={(e) => patchScope(scope, { enabled: e.target.checked })} />
            }
            label="Включить автоматическую генерацию"
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Максимальное количество генераций за сессию"
            inputProps={{ min: 1, max: 100 }}
            value={values.maxGenerations}
            onChange={(e) => patchScope(scope, { maxGenerations: num(e.target.value) })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Количество текстов для генерации за один проход"
            inputProps={{ min: 1, max: 50 }}
            value={values.textsPerRun}
            onChange={(e) => patchScope(scope, { textsPerRun: num(e.target.value) })}
          />
          {scope !== 'brands' && (
            <TextField
              fullWidth
              margin="normal"
              type="number"
              label={`Параллельных запросов к API (${SCOPE_LABEL[scope]})`}
              helperText="0 — использовать значение «за один проход»"
              inputProps={{ min: 0, max: 30 }}
              value={values.maxConcurrentGenerations}
              onChange={(e) => patchScope(scope, { maxConcurrentGenerations: num(e.target.value) })}
            />
          )}
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Задержка между генерациями (мс)"
            inputProps={{ min: 1000, max: 60000, step: 1000 }}
            value={values.delayBetweenGenerations}
            onChange={(e) => patchScope(scope, { delayBetweenGenerations: num(e.target.value) })}
          />
        </SectionCard>

        <SectionCard title="Настройки повторных попыток">
          <FormControlLabel
            control={
              <Switch checked={!!values.autoRetry} onChange={(e) => patchScope(scope, { autoRetry: e.target.checked })} />
            }
            label="Автоматически повторять неудачные попытки"
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Количество попыток повтора"
            inputProps={{ min: 1, max: 10 }}
            value={values.retryAttempts}
            onChange={(e) => patchScope(scope, { retryAttempts: num(e.target.value) })}
          />
        </SectionCard>

        <SectionCard title="Дополнительные настройки">
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Пользовательские инструкции для робота"
            placeholder="Введите дополнительные инструкции для автоматической генерации..."
            value={values.customInstructions || ''}
            onChange={(e) => patchScope(scope, { customInstructions: e.target.value })}
          />
        </SectionCard>
      </Stack>
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={loadAll} disabled={loading}>
          Обновить
        </Button>
        <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ position: 'relative', minHeight: 120 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {!loading && (
          <>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v as Scope)} sx={{ mb: 2 }}>
              {scopes.map((s) => (
                <Tab key={s} value={s} label={SCOPE_LABEL[s]} />
              ))}
            </Tabs>
            {renderRobotFields(activeTab)}
          </>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <SectionCard title="Справка">
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Вкладки «Категории»/«Ветки»: департамент по умолчанию используется сервером, если массовый запуск идёт
            без параметра site. Тексты сохраняются в Mongo с привязкой к выбранному site_host.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Вкладка «Бренды»: те же параметры лимитов и задержек применяются к роботу брендов (отдельный документ
            настроек).
          </Typography>
        </Stack>
      </SectionCard>
    </Box>
  );
};

export default RobotSettings;
